import { ReactNode, useEffect, useRef, useState } from 'react';

/**
 * A header row that stays in view while the page scrolls.
 *
 * `position: sticky` does not work here: the content panel clips horizontally (`overflow-x-auto`),
 * which makes it the sticky container although it never scrolls vertically. So the row switches to
 * `position: fixed` at the top edge once its natural place has scrolled out (the page frame's top bar
 * scrolls with the content, so it is already gone by then), over the same column (left and
 * width taken from its slot), and a slot of the same height keeps the layout from jumping.
 */
export default function StickyHeader(properties: { className?: string; children: ReactNode }) {
    const slot = useRef<HTMLDivElement>(null);
    const row = useRef<HTMLDivElement>(null);
    const [docked, setDocked] = useState<{ left: number; width: number; height: number } | null>(null);

    useEffect(() => {
        const update = () => {
            const slotElement = slot.current;
            const rowElement = row.current;

            if (!slotElement || !rowElement) {
                return;
            }

            const rect = slotElement.getBoundingClientRect();

            if (rect.top < 0) {
                setDocked((previous) => {
                    const next = { left: rect.left, width: rect.width, height: previous?.height ?? rowElement.offsetHeight };

                    return previous && previous.left === next.left && previous.width === next.width ? previous : next;
                });
            } else {
                setDocked(null);
            }
        };

        // Scroll events do not bubble; the capture phase sees whichever element scrolls.
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        update();

        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, []);

    return (
        <div ref={slot} style={docked ? { height: docked.height } : undefined}>
            <div
                ref={row}
                className={properties.className}
                style={docked
                    ? { position: 'fixed', top: 0, left: docked.left, width: docked.width, zIndex: 30 }
                    : undefined}
            >
                {properties.children}
            </div>
        </div>
    );
}
